import { useEffect } from 'react';

interface PhotoViewerProps {
  photos: string[];
  index: number | null;
  label: string;
  onClose: () => void;
  onChange: (index: number) => void;
}

export default function PhotoViewer({ photos, index, label, onClose, onChange }: PhotoViewerProps) {
  useEffect(() => {
    if (index === null) return undefined;

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') onChange((index + 1) % photos.length);
      if (event.key === 'ArrowLeft') onChange((index - 1 + photos.length) % photos.length);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [index, onClose, onChange, photos.length]);

  if (index === null || !photos[index]) return null;

  const total = photos.length;
  const goPrev = () => onChange((index - 1 + total) % total);
  const goNext = () => onChange((index + 1) % total);

  return (
    <div
      className="fixed inset-0 z-50 bg-navy-950/95 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative w-full max-w-6xl max-h-[90vh] flex items-center justify-center"
        onClick={(event) => event.stopPropagation()}
        role="presentation"
      >
        <button
          type="button"
          onClick={goPrev}
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors duration-300 z-10 cursor-pointer"
          aria-label="Previous photo"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>

        <img
          src={photos[index]}
          alt={`${label} — photograph ${index + 1}`}
          className="w-full h-auto max-h-[85vh] object-contain rounded-xl"
        />

        <button
          type="button"
          onClick={goNext}
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors duration-300 z-10 cursor-pointer"
          aria-label="Next photo"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>

        <button
          type="button"
          onClick={onClose}
          className="absolute -top-4 -right-4 w-11 h-11 rounded-full bg-white hover:bg-gold-400 text-navy-900 hover:text-white flex items-center justify-center transition-colors duration-300 z-10 cursor-pointer"
          aria-label="Close photo viewer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-4 px-4 py-3 bg-navy-950/70 backdrop-blur-sm rounded-b-xl">
          <span className="text-xs text-cream-200 truncate max-w-[50%]">{label}</span>
          <span className="text-xs text-white/70 shrink-0">
            {index + 1} / {total}
          </span>
        </div>
      </div>
    </div>
  );
}